import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CalendarDaysIcon, ClockIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import BlogImage from '../../components/common/BlogImage'
import { Blog } from '../../types'
import { blogsApi } from '../../services/api'

const PAGE_SIZE = 9

const formatDate = (value?: string) => {
  if (!value) return 'Not published'
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const BlogCard: React.FC<{ blog: Blog }> = ({ blog }) => {
  const excerpt = blog.short_description || blog.summary || 'Read this market insight from AnalyzerView.'
  return (
    <Card className="h-full !bg-dark-900/70 border border-dark-700 hover:border-primary-500/40 transition-colors">
      <div className="flex flex-col h-full">
        <div className="aspect-[16/10] rounded-xl overflow-hidden bg-dark-800 border border-dark-700">
          <BlogImage
            src={blog.featured_image}
            alt={blog.title}
            className="w-full h-full object-cover"
            fallbackClassName="w-full h-full flex items-center justify-center text-sm text-gray-500"
            fallbackText="No image"
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-300 border border-primary-500/30">
            {blog.category || 'General'}
          </span>
          <span className="text-gray-500">{blog.read_time || '5 min read'}</span>
        </div>

        <h3 className="mt-3 text-xl font-bold text-white line-clamp-2">{blog.title}</h3>
        <p className="mt-2 text-sm text-gray-400 line-clamp-3">{excerpt}</p>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <UserIcon className="w-3.5 h-3.5" />
            {blog.author || 'AnalyzerView Desk'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDaysIcon className="w-3.5 h-3.5" />
            {formatDate(blog.published_at || blog.created_at)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ClockIcon className="w-3.5 h-3.5" />
            {blog.read_time || '5 min read'}
          </span>
        </div>

        <div className="mt-5 pt-4 border-t border-dark-700">
          <Link to={`/blogs/${blog.slug}`}>
            <Button size="sm" className="w-full">Read More</Button>
          </Link>
        </div>
      </div>
    </Card>
  )
}

const BlogsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState<Blog[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [totalPages, setTotalPages] = useState(0)

  const page = Number(searchParams.get('page') || '1') || 1
  const search = searchParams.get('search') || ''
  const category = searchParams.get('category') || ''

  const [searchInput, setSearchInput] = useState(search)

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    blogsApi.getCategories().then(setCategories).catch(() => setCategories([]))
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')

    blogsApi.getPublishedBlogs({
      page,
      page_size: PAGE_SIZE,
      search: search || undefined,
      category: category || undefined,
    })
      .then((res) => {
        if (!mounted) return
        setItems(res.items)
        setTotalPages(res.total_pages)
      })
      .catch(() => {
        if (!mounted) return
        setItems([])
        setTotalPages(0)
        setError('Failed to load blogs. Please try again.')
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => { mounted = false }
  }, [page, search, category])

  const updateParams = (next: { page?: number; search?: string; category?: string }) => {
    const params = new URLSearchParams(searchParams)
    const nextPage = String(next.page ?? page)
    const nextSearch = next.search ?? search
    const nextCategory = next.category ?? category

    if (nextPage === '1') params.delete('page')
    else params.set('page', nextPage)

    if (nextSearch.trim()) params.set('search', nextSearch.trim())
    else params.delete('search')

    if (nextCategory.trim()) params.set('category', nextCategory.trim())
    else params.delete('category')

    setSearchParams(params)
  }

  const canPrev = page > 1
  const canNext = page < totalPages

  const pageWindow = useMemo(() => {
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, page + 2)
    const pages: number[] = []
    for (let i = start; i <= end; i += 1) pages.push(i)
    return pages
  }, [page, totalPages])

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="max-w-3xl">
        <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">Blogs</h1>
        <p className="mt-3 text-gray-400 text-base sm:text-lg">
          Market insights, educational explainers, and strategy notes from the AnalyzerView research team.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateParams({ page: 1, search: searchInput })
            }}
            placeholder="Search blogs by title, excerpt, tags..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-dark-900 border border-dark-700 text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <select
          value={category}
          onChange={(e) => updateParams({ page: 1, category: e.target.value })}
          className="px-3 py-2.5 rounded-xl bg-dark-900 border border-dark-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => updateParams({ page: 1, search: searchInput })}>
          Apply Search
        </Button>
        {(search || category) && (
          <Button size="sm" variant="ghost" onClick={() => updateParams({ page: 1, search: '', category: '' })}>
            Clear Filters
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="mt-8 rounded-xl border border-danger-500/30 bg-danger-500/10 text-danger-300 p-6">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dark-700 bg-dark-900/60 text-gray-400 p-10 text-center">
          No blogs matched your filters.
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((blog) => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => updateParams({ page: page - 1 })}>
              Previous
            </Button>

            {pageWindow.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => updateParams({ page: p })}
                className={`w-9 h-9 rounded-lg text-sm font-semibold border transition-colors ${
                  p === page
                    ? 'border-primary-500 bg-primary-500/20 text-primary-300'
                    : 'border-dark-700 bg-dark-900 text-gray-300 hover:border-dark-500'
                }`}
              >
                {p}
              </button>
            ))}

            <Button size="sm" variant="outline" disabled={!canNext} onClick={() => updateParams({ page: page + 1 })}>
              Next
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

export default BlogsPage
